import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import { btn } from "../../../../components/Button/buttonStyles";

const HEADERS = [
  "Code",
  "Name",
  "Role",
  "Client Pool",
  "Gender",
  "Email",
  "Phone",
  "Status",
  "Last Login",
];

const ManagersPage = () => {
  const navigate = useNavigate();
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const response = await api.get("/managers", { withCredentials: true });
        setManagers(response.data.data);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch managers");
      } finally {
        setLoading(false);
      }
    };

    fetchManagers();
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Managers</h1>
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => navigate("/managers/create-manager")}
            className={btn("primary", "sm")}
          >
            Add Manager
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
        </div>
      )}
      {error && <p className="text-danger-600 text-sm">{error}</p>}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{managers.length}</span> items.
          </p>

          <div className="w-full bg-white shadow rounded-lg overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[1100px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    {HEADERS.map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {managers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={HEADERS.length}
                        className="py-10 text-center text-gray-400 text-xs"
                      >
                        No managers found.
                      </td>
                    </tr>
                  ) : (
                    managers.map((manager, index) => {
                      const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                      const fullName = `${manager.first_name || ""} ${manager.last_name || ""}`.trim();
                      return (
                        <tr
                          key={manager.id}
                          className={`${rowBg} hover:bg-brand-50 transition-colors`}
                        >
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <Link
                              to={`/managers/${manager.id}`}
                              className="text-brand-600 hover:text-brand-800 underline font-medium"
                            >
                              {`MGR-${String(manager.id).padStart(3, "0")}`}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-900">
                            {fullName || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {manager.role?.role_details?.name || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {manager.client_pool?.name || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700 capitalize">
                            {manager.gender || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            {manager.auth?.email ? (
                              <a
                                href={`mailto:${manager.auth.email}`}
                                className="text-brand-600 hover:underline"
                              >
                                {manager.auth.email}
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {manager.phone || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <span
                              className={`px-2 py-0.5 inline-block rounded-full text-[10px] font-semibold ${
                                manager.can_login
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-200 text-gray-700"
                              }`}
                            >
                              {manager.can_login ? "Enabled" : "Disabled"}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-500">
                            —
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ManagersPage;
